<script setup lang="ts">
import { reactive, ref } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import { ElMessage } from "element-plus";
import axios from "axios";
import router from "../router";
import { useAuthStore } from "../store/authStore";
import OTPVerification from './OTPVerification.vue';
import AuthSlideshow from './AuthSlideshow.vue';
import type { EpPropMergeType } from 'element-plus/es/utils';

interface RuleForm { email: string; password: string; }

const loading = ref(false);
const formSize = ref<EpPropMergeType<StringConstructor, "" | "default" | "small" | "large", unknown>>("default");
const formError = ref("");
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive<RuleForm>({ email: "", password: "" });
const store = useAuthStore();
const showOTP = ref(false);
const otpEmail = ref('');
const loginResponse = ref<any>(null);

const rules = reactive<FormRules<RuleForm>>({
  email:    [{ required: true, message: "Please enter your email address.", trigger: "blur" }],
  password: [{ required: true, message: "Please enter your password.", trigger: "blur" }],
});

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate((valid) => { if (valid) { loading.value = true; postData(); } });
};

const sendLoginOTP = async (email: string) => {
  return axios.post(`${import.meta.env.VITE_ONBOARDING_SERVICE}send_otp`, {
    email,
    context: "login",
  });
};

const postData = async () => {
  try {
    formError.value = "";
    const response = await axios.post(`${import.meta.env.VITE_ONBOARDING_SERVICE}login`, {
      email: ruleForm.email, password: ruleForm.password,
    });

    if (response.status == 200 && response.data.status) {
      try {
        await sendLoginOTP(ruleForm.email);
      } catch {
        formError.value = "Too many OTP requests. Please wait before trying again.";
        return;
      }

      otpEmail.value = ruleForm.email;
      loginResponse.value = response.data;
      showOTP.value = true;
    } else {
      formError.value = "Login failed, please check your email and password.";
    }
  } catch {
    formError.value = "Something went wrong, please try again.";
  } finally {
    loading.value = false;
  }
};

const completeLogin = () => {
  if (loginResponse.value) {
    registerSession(loginResponse.value);
    if (loginResponse.value.data.user_phone == "") {
      ElMessage({ message: "Welcome aboard! Please complete your profile.", type: "success" });
      router.push("/profile");
    } else {
      router.push("/request");
    }
  }
};

const registerSession = (data: any) => {
  const jwt = data.jsonwebtoken;
  localStorage.setItem("_panda", jwt);
  store.setAuthStatus(true);
  store.setToken(jwt);
  store.setUserData(data.data);
};
</script>

<template>
  <div class="auth-page">
    <AuthSlideshow />
    <div class="auth-right">
      <div class="form-container" v-if="!showOTP">
        <div class="form-header">
          <h2 class="form-title">Welcome back</h2>
          <p class="form-subtitle">Sign in to your account to continue</p>
        </div>
        <el-form ref="ruleFormRef" :model="ruleForm" :rules="rules"
          label-width="0px" class="auth-form" :size="formSize" v-loading="loading">
          <div v-if="formError" class="error-banner">
            <span class="error-icon">!</span>{{ formError }}
          </div>
          <div class="field-group">
            <label class="field-label">Email address</label>
            <el-form-item prop="email">
              <el-input type="email" v-model="ruleForm.email" placeholder="you@company.com" size="large" />
            </el-form-item>
          </div>
          <div class="field-group">
            <div class="label-row">
              <label class="field-label">Password</label>
              <router-link to="/forgot" class="forgot-link">Forgot password?</router-link>
            </div>
            <el-form-item prop="password">
              <el-input type="password" v-model="ruleForm.password" placeholder="Enter your password" size="large" autocomplete="off" show-password />
            </el-form-item>
          </div>
          <el-form-item>
            <button class="submit-btn" @click="submitForm(ruleFormRef)" :disabled="loading">
              <span v-if="!loading">Sign in</span><span v-else>Signing in…</span>
            </button>
          </el-form-item>
          <div class="bottom-link">
            Don't have an account?
            <router-link to="/signup" class="nav-link-inline">Create one</router-link>
          </div>
        </el-form>
      </div>
      <OTPVerification
        v-if="showOTP" :email="otpEmail" context="login"
        @verified="() => { showOTP = false; completeLogin(); }"
        @close="() => { showOTP = false; }"
      />
    </div>
  </div>
</template>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display&display=swap');

.auth-page { display: flex; min-height: 100vh; font-family: 'DM Sans', sans-serif; }

.auth-right {
  flex: 1; min-width: 0; min-height: 100vh; display: flex; align-items: center;
  justify-content: center; background: #fafafa; padding: 3rem 2rem;
}

.form-container { width: 100%; max-width: 400px; animation: fadeUp 0.4s ease both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.form-header { margin-bottom: 2.5rem; }
.form-title { font-family: 'DM Sans', sans-serif; font-size: 2rem; font-weight: 700; color: #0f1c2e; margin: 0 0 0.5rem 0; letter-spacing: -0.02em; }
.form-subtitle { font-size: 0.9rem; color: #8a94a6; margin: 0; line-height: 1.6; }

.error-banner { display: flex; align-items: center; gap: 0.6rem; background: #fff5f5; border: 1px solid #fdd; border-radius: 8px; padding: 0.75rem 1rem; font-size: 0.85rem; color: #c0392b; margin-bottom: 1.5rem; }
.error-icon { width: 18px; height: 18px; background: #e74c3c; color: white; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.7rem; font-weight: 700; flex-shrink: 0; }

.field-group { margin-bottom: 1.25rem; }
.label-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.4rem; }
.field-label { display: block; font-size: 0.82rem; font-weight: 500; color: #374151; margin-bottom: 0.4rem; letter-spacing: 0.01em; }
.forgot-link { font-size: 0.8rem; color: #12284A; text-decoration: none; opacity: 0.7; transition: opacity 0.15s; }
.forgot-link:hover { opacity: 1; }

.auth-form :deep(.ep-input__wrapper),
.auth-form :deep(.el-input__wrapper) { border-radius: 8px !important; border: 1.5px solid #e5e7eb !important; box-shadow: none !important; background: white !important; transition: border-color 0.2s; }
.auth-form :deep(.ep-input__wrapper:hover),
.auth-form :deep(.el-input__wrapper:hover) { border-color: #b0bec5 !important; }
.auth-form :deep(.ep-input__wrapper.is-focus),
.auth-form :deep(.el-input__wrapper.is-focus) { border-color: #12284A !important; }
.auth-form :deep(.ep-form-item),
.auth-form :deep(.el-form-item) { margin-bottom: 0; }
.auth-form :deep(input:-webkit-autofill),
.auth-form :deep(input:-webkit-autofill:hover),
.auth-form :deep(input:-webkit-autofill:focus) { -webkit-box-shadow: 0 0 0px 1000px white inset !important; box-shadow: 0 0 0px 1000px white inset !important; -webkit-text-fill-color: #374151 !important; transition: background-color 5000s ease-in-out 0s; }

.phone-input { width: 100%; }
.auth-form :deep(.m-phone-number-input) { width: 100%; }

.terms-group { margin-top: 0.5rem; }
.terms-label { display: flex; align-items: flex-start; gap: 0.6rem; cursor: pointer; }
.terms-text { font-size: 0.84rem; color: #374151; line-height: 1.5; margin-top: 2px; }
.terms-link { color: #12284A; text-decoration: underline; }

.submit-btn { width: 100%; height: 46px; background: #12284A; color: white; border: none; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 500; cursor: pointer; margin-top: 0.75rem; transition: background 0.2s, transform 0.1s; }
.submit-btn:hover:not(:disabled) { background: #1a3a6a; }
.submit-btn:active:not(:disabled) { transform: scale(0.99); }
.submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.bottom-link { text-align: center; margin-top: 1.75rem; font-size: 0.87rem; color: #8a94a6; padding-bottom: 1rem; }
.nav-link-inline { color: #12284A; font-weight: 500; text-decoration: none; margin-left: 0.25rem; }
.nav-link-inline:hover { text-decoration: underline; }
.nav-link { font-size: 0.87rem; color: #12284A; text-decoration: none; opacity: 0.7; transition: opacity 0.15s; }
.nav-link:hover { opacity: 1; }

.success-block { text-align: center; animation: fadeUp 0.4s ease both; }
.success-icon { width: 56px; height: 56px; background: #12284A; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; margin: 0 auto 1.5rem; }
.success-block .form-title { margin-bottom: 0.75rem; }
.success-block .form-subtitle { margin-bottom: 2rem; }
.back-link { font-size: 0.87rem; color: #12284A; text-decoration: none; font-weight: 500; }
.back-link:hover { text-decoration: underline; }

@media (max-width: 900px) {
  .auth-right { background: white; }
}
</style>